import { useBunja } from 'bunja/react';
import { useAtomValue, useSetAtom } from 'jotai';
import { useTranslations } from 'next-intl';
import { useCallback, useEffect } from 'react';

import { CurrencyDisplay } from '@flipster/features/src/misc/display';
import { authBunja } from '@flipster/features/src/shared/auth/state';
import { earnSummaryBunja, getEarnSummaryOfCurrency } from '@flipster/features/src/shared/earn/state';
import { useKYC } from '@flipster/features/src/shared/kyc';
import { useDepositModal } from '@flipster/features/src/shared/wallet-modal/deposit';
import { useRouter } from '@flipster/i18n/routing';
import { Modal } from '@flipster/react/design-system-legacy/modal';
import { Badge } from '@flipster/react/design-system/badge';
import { Dialog, dialog } from '@flipster/react/design-system/dialog';
import ArrowDownIcon from '@flipster/react/icon/generated/ArrowDownIcon';
import CandlestickIcon from '@flipster/react/icon/generated/CandlestickIcon';
import ChevronRightIcon from '@flipster/react/icon/generated/ChevronRightIcon';
import PlusIcon from '@flipster/react/icon/generated/PlusIcon';
import { css } from '@flipster/styled-system/css';
import { hstack, stack } from '@flipster/styled-system/patterns';
import segment, { WalletEvent } from '@flipster/utils/segment';
import { EarnEvent } from '@flipster/utils/segment/events/earn';

import { mintBunja } from '@web/app/[locale]/(web)/_modal/MintFlowModal/state';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  currency: string;
  onExit: () => void;
}

export const EarnActionModal = ({ isOpen, onClose, currency, onExit }: Props) => {
  const t = useTranslations();
  const router = useRouter();
  const openDepositModal = useDepositModal();
  const { isLoggedInAtom, userAtom } = useBunja(authBunja);
  const { earnSummaryAtom } = useBunja(earnSummaryBunja);
  const { providerCurrencyAtom, openMintModal, collateralCurrenciesAtom } = useBunja(mintBunja);
  const isLoggedIn = useAtomValue(isLoggedInAtom);
  const earnSummary = useAtomValue(earnSummaryAtom);
  const collateralCurrencies = useAtomValue(collateralCurrenciesAtom);
  const user = useAtomValue(userAtom);
  const setProviderCurrency = useSetAtom(providerCurrencyAtom);
  const hasFirstDeposit = user && user.firstDepositAt;

  const { kycInfo, isAchievedLevel } = useKYC();

  const { isTradeAvailable, isMintAvailable, appliedAprPct, maxAprPct } = getEarnSummaryOfCurrency(
    currency,
    earnSummary,
  );

  const checkLoggedIn = () => {
    onClose();
    if (!isLoggedIn) {
      router.push('/login');
    }
    return isLoggedIn;
  };

  const openDeposit = () => {
    if (!checkLoggedIn()) return;
    segment.track(EarnEvent.EarnManageDetailsClick, { manage_type: 'DEPOSIT', currency });
    openDepositModal({ defaultValues: { currency } });
  };

  const openMint = () => {
    if (!checkLoggedIn()) return;
    if (!checkKycValidation()) return;
    segment.track(EarnEvent.EarnManageDetailsClick, { manage_type: 'MINT', currency });
    openMintModal({ providerCurrency: currency });
  };

  const openTrade = () => {
    if (!checkLoggedIn()) return;
    segment.track(EarnEvent.EarnManageDetailsClick, { manage_type: 'TRADE', currency });
    router.push(`/trade/spot/${currency}USDT`);
  };

  // TODO: [sh] KYC 확인 로직을 따로 두면 좋을듯
  const checkKycValidation = useCallback(() => {
    if (isAchievedLevel) return true;

    dialog.open({
      title: {
        label: t('Assets.r250401.Identity_verification'),
      },
      content: t('Assets.r250401.Identity_verification_required_description'),
      buttons: {
        primary: (
          <Dialog.Button
            hierarchy='primary'
            label={t('Assets.r250401.Verify_now')}
            onClick={() => {
              segment.track(WalletEvent.KycVerificationRequireClick, { kyc_level: kycInfo.currentKYCLevel });
              router.push('/user/kyc');
            }}
          />
        ),
        secondary: (
          <Dialog.Button
            hierarchy='secondary'
            color='neutral'
            label={t('Common.r250401.Close')}
            onClick={() => {
              segment.track(WalletEvent.VerifyCancelButton);
            }}
          />
        ),
      },
    });

    return false;
  }, [isAchievedLevel, kycInfo, router, t]);

  useEffect(() => {
    setProviderCurrency(currency);
  }, [currency, setProviderCurrency]);

  return (
    <Modal isOpen={isOpen} onExit={onExit}>
      <Modal.Close onClose={onClose} />
      <Modal.Header>
        <Modal.Title
          label={
            <CurrencyDisplay
              currency={currency}
              icon={<CurrencyDisplay.Icon size={32} space={8} />}
              mainLabel={
                <div className={hstack({ gap: 8 })}>
                  <CurrencyDisplay.Label value='shortName' />
                  <Badge color='positive' type='outline'>
                    {isLoggedIn && hasFirstDeposit
                      ? t('Common.r251008.Apr_percent_apr', {
                          apr: appliedAprPct.toString(),
                        })
                      : t('Common.r251008.Up_to_apr_percent_apr', {
                          apr: maxAprPct.toString(),
                        })}
                  </Badge>
                </div>
              }
              css={{ textStyle: 'Text/M' }}
            />
          }
        />
      </Modal.Header>
      <Modal.Content>
        <ActionItem icon={<ArrowDownIcon size={24} />} label={t('Navigation.r250401.Deposit')} onClick={openDeposit} />
        {isMintAvailable && (
          <ActionItem
            icon={<PlusIcon size={24} />}
            label={t('Mint.r251008.Mint')}
            onClick={openMint}
            description={t('Assets.r251008.Mint_provider_currency_amount_collateral_currency', {
              providerCurrency: currency,
              collateralCurrencyWithAmount: collateralCurrencies.map((c) => c.currency).join(' or '),
            })}
          />
        )}
        {isTradeAvailable && (
          <ActionItem icon={<CandlestickIcon size={24} />} label={t('Navigation.r250401.Trade')} onClick={openTrade} />
        )}
      </Modal.Content>
    </Modal>
  );
};

const ActionItem = ({
  icon,
  label,
  onClick,
  description,
}: {
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
  description?: string;
}) => {
  return (
    <div
      className={stack({
        gap: 8,
        textStyle: 'Text/L/Regular',
        color: 'content.default.level-5',
        py: 24,
        cursor: 'pointer',
      })}
    >
      <div className={hstack({ justify: 'space-between' })} onClick={onClick}>
        <div className={hstack({ gap: 8 })}>
          {icon} {label}
        </div>
        <ChevronRightIcon size={14} />
      </div>
      {description && (
        <p className={css({ textStyle: 'Text/M/Regular', color: 'content.default.level-2' })}>{description}</p>
      )}
    </div>
  );
};
